import { supabase } from './supabaseClient';

export interface CountyData {
  name: string;
  region: string;
  climate: {
    temperature: string;
    rainfall: string;
    altitude: string;
  };
  mainCrops: string[];
  governmentRequirements: {
    licenses: string[];
    permits: string[];
    regulations: string[];
  };
  marketPrices: {
    commodity: string;
    price: number;
    unit: string;
    date: string;
  }[];
  farmingTips: {
    category: string;
    tips: string[];
  }[];
  resources: {
    title: string;
    url: string;
    description: string;
  }[];
  lastUpdated: Date;
  dataSource?: string;
  sourceCitations?: string[];
}

export interface CropData {
  cropName: string;
  countyData: Record<string, number>;
  nationalAverage: number;
  internationalComparison: Record<string, number>;
  marketTrend: Array<{month: string, price: number}>;
  growingConditions: Record<string, string>;
  bestPractices: string[];
  dataSource: string;
  sourceCitations?: string[];
}

const transformCountyData = (dbData: any): CountyData => {
  return {
    name: dbData.name,
    region: dbData.region,
    climate: {
      temperature: dbData.temperature_range,
      rainfall: dbData.rainfall_range,
      altitude: dbData.altitude_range,
    },
    mainCrops: dbData.main_crops || [],
    governmentRequirements: {
      licenses: dbData.required_licenses || [],
      permits: dbData.required_permits || [],
      regulations: dbData.applicable_regulations || [],
    },
    marketPrices: (dbData.county_market_prices || []).map((price: any) => ({
      commodity: price.commodity,
      price: Number(price.price),
      unit: price.unit,
      date: price.date,
    })),
    farmingTips: (dbData.county_farming_tips || []).map((tip: any) => ({
      category: tip.category,
      tips: tip.tips || [],
    })),
    resources: (dbData.county_resources || []).map((resource: any) => ({
      title: resource.title,
      url: resource.url,
      description: resource.description,
    })),
    lastUpdated: new Date(dbData.updated_at || dbData.created_at),
    dataSource: dbData.data_source,
    sourceCitations: dbData.source_citations,
  };
};

const transformCropData = (dbData: any): CropData => {
  const growingConditionsRow = Array.isArray(dbData.crop_growing_conditions) ? dbData.crop_growing_conditions[0] : null;
  const bestPracticesRow = Array.isArray(dbData.crop_best_practices) ? dbData.crop_best_practices[0] : null;

  return {
    cropName: dbData.name,
    countyData: (dbData.crop_county_data || []).reduce((acc: Record<string, number>, row: any) => {
      if (row?.county_name) acc[row.county_name] = Number(row.yield_score);
      return acc;
    }, {}),
    nationalAverage: Number(dbData.national_average || 0),
    internationalComparison: dbData.international_comparison || {},
    marketTrend: (dbData.crop_market_trends || []).map((trend: any) => ({
      month: trend.month,
      price: Number(trend.price),
    })),
    growingConditions: {
      temperature: growingConditionsRow?.temperature || '',
      rainfall: growingConditionsRow?.rainfall || '',
      soil: growingConditionsRow?.soil || '',
      altitude: growingConditionsRow?.altitude || '',
    },
    bestPractices: bestPracticesRow?.practices || [],
    dataSource: dbData.data_source || 'Database',
    sourceCitations: dbData.source_citations,
  };
};

export const countyService = {
  async getAllCounties(): Promise<CountyData[]> {
    try {
      const { data, error } = await supabase
        .from('counties')
        .select(`
          *,
          county_market_prices(*),
          county_farming_tips(*),
          county_resources(*)
        `);
      if (error) throw error;
      return (data || []).map(transformCountyData);
    } catch (error) {
      console.error('Error fetching counties:', error);
      throw error;
    }
  },

  async getCountyByName(name: string): Promise<CountyData> {
    try {
      const { data, error } = await supabase
        .from('counties')
        .select(`
          *,
          county_market_prices(*),
          county_farming_tips(*),
          county_resources(*)
        `)
        .ilike('name', name)
        .single();
      if (error) throw error;
      return transformCountyData(data);
    } catch (error) {
      console.error(`Error fetching county ${name}:`, error);
      throw error;
    }
  },

  async updateCounty(name: string, data: Partial<CountyData>): Promise<CountyData> {
    try {
      const updatePayload: any = {};
      if (data.name !== undefined) updatePayload.name = data.name;
      if (data.region !== undefined) updatePayload.region = data.region;
      if (data.climate?.temperature !== undefined) updatePayload.temperature_range = data.climate.temperature;
      if (data.climate?.rainfall !== undefined) updatePayload.rainfall_range = data.climate.rainfall;
      if (data.climate?.altitude !== undefined) updatePayload.altitude_range = data.climate.altitude;
      if (data.mainCrops !== undefined) updatePayload.main_crops = data.mainCrops;
      if (data.governmentRequirements?.licenses !== undefined) updatePayload.required_licenses = data.governmentRequirements.licenses;
      if (data.governmentRequirements?.permits !== undefined) updatePayload.required_permits = data.governmentRequirements.permits;
      if (data.governmentRequirements?.regulations !== undefined) updatePayload.applicable_regulations = data.governmentRequirements.regulations;

      const { data: updated, error } = await supabase
        .from('counties')
        .update({ ...updatePayload, updated_at: new Date().toISOString() })
        .ilike('name', name)
        .select(`
          *,
          county_market_prices(*),
          county_farming_tips(*),
          county_resources(*)
        `)
        .single();
      if (error) throw error;
      return transformCountyData(updated);
    } catch (error) {
      console.error(`Error updating county ${name}:`, error);
      throw error;
    }
  },

  async getCropData(name: string): Promise<CropData> {
    try {
      const { data, error } = await supabase
        .from('crop_encyclopedia')
        .select(`
          *,
          crop_county_data(*),
          crop_market_trends(*),
          crop_growing_conditions(*),
          crop_best_practices(*)
        `)
        .ilike('name', name)
        .single();
      if (error) throw error;
      return transformCropData(data);
    } catch (error) {
      console.error(`Error fetching crop ${name}:`, error);
      throw error;
    }
  },
}; 
